/**
 * Hand-written types mirroring the OpenAPI spec at libs/api-spec/openapi.yaml.
 * Will be replaced by orval-generated types once codegen runs in CI.
 * For now this keeps the web app moving without requiring node_modules.
 */

export type ProjectType = "mobile" | "web" | "component" | "backend";
export type UserTier = "free" | "plus" | "pro" | "enterprise";

export interface HealthStatus {
  readonly ok: boolean;
  readonly version: string;
  readonly timestamp?: string;
}

export interface PublicUser {
  readonly id: string;
  readonly handle: string;
  readonly email: string;
  readonly name: string | null;
  readonly avatarUrl: string | null;
  readonly tier: UserTier;
}

export interface AuthMeResponse {
  readonly user: PublicUser | null;
}

export interface CreateProjectInput {
  readonly prompt: string;
  readonly type: ProjectType;
  readonly attachmentUploadIds?: ReadonlyArray<string>;
}

export interface Project {
  readonly id: string;
  readonly ownerId: string;
  readonly name: string;
  readonly type: ProjectType;
  readonly description: string | null;
  readonly region: string;
  readonly lastPreviewBuildHash: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ProjectList {
  readonly items: ReadonlyArray<Project>;
  readonly nextCursor: string | null;
}

export interface PublishedProject extends Project {
  readonly slug: string;
  readonly visibility: "public" | "private";
  readonly publishedAt: string;
}

/**
 * RFC 9457 Problem Details, in our shape. `type` defaults to "about:blank"
 * until we ship a docs site. Switch on `code` for branching logic; `title`
 * is for humans.
 */
export interface Problem {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly code: ProblemCode;
  readonly detail?: string;
}

export type ProblemCode =
  | "validation"
  | "unauthenticated"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "quota_exceeded"
  | "server_error"
  | "unknown";

export interface ValidationErrorResponse extends Problem {
  readonly code: "validation";
  readonly errors: ReadonlyArray<{
    readonly path: string;
    readonly message: string;
  }>;
}

export interface ProjectFileMeta {
  readonly path: string;
  readonly size: number;
  readonly modifiedAt: string;
}

export interface ProjectFileList {
  readonly items: ReadonlyArray<ProjectFileMeta>;
}

export interface ProjectFileContents {
  readonly path: string;
  readonly content: string;
  readonly size: number;
  readonly modifiedAt: string;
}

export interface AiTurnSummary {
  readonly id: string;
  readonly prompt: string;
  readonly assistantText: string;
  readonly routeKey: string;
  readonly provider: string;
  readonly finishReason: "stop" | "tool_use" | "length" | "error";
  readonly iterations: number;
  readonly durationMs: number;
  readonly toolCallCount: number;
  readonly errorMessage: string | null;
  readonly createdAt: string;
}

export interface AiTurnList {
  readonly items: ReadonlyArray<AiTurnSummary>;
}

export interface ProjectMemberSummary {
  readonly userId: string;
  readonly email: string;
  readonly handle: string;
  readonly role: "editor" | "viewer";
  readonly addedAt: string;
}

export interface ProjectInviteSummary {
  readonly id: string;
  readonly email: string;
  readonly role: "editor" | "viewer";
  readonly token: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

export interface ProjectMembersResponse {
  readonly owner: { userId: string; email: string; handle: string } | null;
  readonly members: ReadonlyArray<ProjectMemberSummary>;
  readonly invites: ReadonlyArray<ProjectInviteSummary>;
}

export interface CreateInviteResult {
  readonly id: string;
  readonly email: string;
  readonly role: "editor" | "viewer";
  readonly token: string;
  readonly expiresAt: string;
  readonly autoAccepted: boolean;
}

export interface InvitePreview {
  readonly projectId: string;
  readonly projectName: string;
  readonly ownerHandle: string;
  readonly email: string;
  readonly role: "editor" | "viewer";
  readonly expiresAt: string;
  readonly accepted: boolean;
  readonly expired: boolean;
}

export interface AcceptInviteResult {
  readonly ok: boolean;
  readonly projectId: string;
  readonly alreadyMember: boolean;
}

export interface SharedProjectSummary {
  readonly id: string;
  readonly name: string;
  readonly type: ProjectType;
  readonly description: string | null;
  readonly updatedAt: string;
  readonly role: "editor" | "viewer";
}

export interface SharedProjectList {
  readonly items: ReadonlyArray<SharedProjectSummary>;
}

export interface AuditEvent {
  readonly kind: string;
  readonly at: string;
  readonly summary: string;
  readonly actor: string | null;
}

export interface AuditLog {
  readonly items: ReadonlyArray<AuditEvent>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | ValidationErrorResponse;
  constructor(status: number, problem: Problem | ValidationErrorResponse) {
    super(problem.title);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}
